<template>
  <Modal title="Kopijuoti kelionę" :is-open="true" @close="closeModal">
    <div class="space-y-4">
      <!-- Alerts -->
      <Alert
        v-if="formData.dates.length == 0"
        text="Pasirinkite bent vieną datą"
        alert-class="alert-info"
        icon="info"
        :dismissible="false"
      />
      <Alert
        v-if="copiedTravels.success_count"
        :text="copiedTravels.message"
        alert-class="alert-success"
        icon="info"
        :dismissible="false"
      />

      <!-- Date Selection -->
      <fieldset class="fieldset" v-if="formData.dates.length < 20">
        <legend class="fieldset-legend">
          Pasirinkite datas, kuriomis norite kopijuoti pasirinktą kelionę
        </legend>
        <input
          type="date"
          v-model="selectedDate"
          @change="addDate"
          class="input w-full"
        />
      </fieldset>

      <!-- Alerts -->
      <Alert
        v-if="overlapingTravels.hasConflict"
        :text="overlapingTravels.message"
        alert-class="alert-warning"
        icon="warning"
        :dismissible="true"
      />
      <Alert
        v-if="copiedTravels.error"
        :text="copiedTravels.message"
        alert-class="alert-error"
        icon="warning"
        :dismissible="true"
      />

      <!-- Selected Dates List -->
      <fieldset class="fieldset" v-if="formData.dates.length > 0">
        <legend class="fieldset-legend">
          Pasirinktos datos ({{ formData.dates.length }})
        </legend>
        <div class="space-y-2 max-h-72 overflow-y-auto">
          <div
            v-for="date in formData.dates"
            :key="date"
            class="flex items-center justify-between px-2 py-1 bg-base-200 rounded-lg"
          >
            <span class="text-sm font-medium">{{ date }}</span>
            <button
              @click="removeDate(date)"
              class="btn btn-ghost btn-sm btn-circle"
              type="button"
            >
              <DefaultIcon name="xMark" size="w-4 h-4" />
            </button>
          </div>
        </div>
      </fieldset>

      <!-- SMS Options -->
      <fieldset class="fieldset">
        <legend class="fieldset-legend">SMS nustatymai</legend>

        <label class="label cursor-pointer justify-start gap-4">
          <input
            type="checkbox"
            v-model="formData.send_sms_registration"
            class="checkbox checkbox-primary checkbox-sm"
          />
          <span>Siųsti SMS registracijos metu</span>
        </label>

        <label class="label cursor-pointer justify-start gap-4">
          <input
            type="checkbox"
            v-model="formData.send_sms_reminder"
            class="checkbox checkbox-primary checkbox-sm"
          />
          <span>Siųsti SMS priminimą</span>
        </label>
      </fieldset>
    </div>

    <template #footer>
      <button
        @click="createTravelCopies"
        class="btn btn-primary mr-2"
        :disabled="formData.dates.length < 1 || isFormSubmitted"
      >
        Sukurti ({{ formData.dates.length }})
      </button>
      <button
        @click="closeModal"
        class="btn btn-ghost"
        :disabled="isFormSubmitted"
      >
        Atšaukti
      </button>
    </template>
  </Modal>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed } from "vue";
import { useTravelStore } from "@/stores/travels";

import Modal from "@/components/ui/modals/GeneralModal.vue";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import Alert from "@/components/ui/alerts/DefaultAlert.vue";

const props = defineProps({
  travelId: {
    type: [String, Number],
    required: true,
  },
  driverId: {
    type: [String, Number],
    required: true,
  },
  vehicleId: {
    type: [String, Number],
    required: true,
  },
  startTime: {
    type: [String, Number],
    required: true,
  },
  finishTime: {
    type: [String, Number],
    required: true,
  },
});

const travelStore = useTravelStore();
const emit = defineEmits(["close"]);

const formData = ref({
  travelId: parseInt(props.travelId),
  dates: [],
  send_sms_registration: false,
  send_sms_reminder: false,
});

const selectedDate = ref("");
const overlapingTravels = ref("");
const copiedTravels = ref("");

const isFormSubmitted = ref(false);
const isSelectedDateFuture = computed(() => {
  return (
    new Date(selectedDate.value) > new Date(new Date().setHours(0, 0, 0, 0))
  );
});

// Add date to the list
const addDate = async () => {
  if (!selectedDate.value) return;

  // Check if date already exists
  if (formData.value.dates.includes(selectedDate.value)) {
    return;
  }

  if (isSelectedDateFuture.value) {
    // check overlaping travels
    const params = {
      date: selectedDate.value,
      driverId: props.driverId,
      vehicleId: props.vehicleId,
      startTime: props.startTime.slice(0, 5),
      finishTime: props.finishTime.slice(0, 5),
    };

    overlapingTravels.value = await travelStore.checkTravelsOverlap(params);
  }

  if (overlapingTravels.value.hasConflict) {
    return;
  } else {
    formData.value.dates.push(selectedDate.value);
    selectedDate.value = "";
  }
};

// Remove date from the list
const removeDate = (dateToRemove) => {
  formData.value.dates = formData.value.dates.filter(
    (date) => date !== dateToRemove,
  );
};

const createTravelCopies = async () => {
  isFormSubmitted.value = true;

  copiedTravels.value = await travelStore.bulkCopy(formData.value);

  if (copiedTravels.value.success_count > 0) {
    setTimeout(() => {
      closeModal();
    }, 5000);
  } else {
    isFormSubmitted.value = false;
    return;
  }
};

const closeModal = () => {
  overlapingTravels.value = "";
  selectedDate.value = "";
  emit("close");
};
</script>
